import {
  type HighlightsController,
  type HighlightsRenderer
} from '@databases/highlights';
import {
  HIGHLIGHT_FILL_HANDLE_CLASS,
  HIGHLIGHT_FILL_HANDLE_TOP_CLASS
} from '@databases/highlights/constants';

import { type SelectionState } from '../../machine/state/types.ts';
import { createEffect } from '../utils/create-effect.ts';

import { FILL_HANDLE_LAYER_Z_INDEX } from './constants.ts';

export interface FillHandleEffectDeps {
  controller: HighlightsController;
  /**
   * Returns the number of rows currently visible in the grid.
   * Used to suppress the fill handle on the last row, where it would be
   * obscured by the sticky "Add Entry" row and cannot be raised above it
   * without breaking the sticky prefix column stacking order.
   */
  getVisibleEntryCount(): number;
}

/**
 * Creates the effect that draws the fill handle (the little blue circle)
 * at the active selection.
 *
 * Listens to the same selection and editing deltas as the highlights effect
 * so the handle stays in sync and hides during selection preview and cell edit.
 * (Redrawing on layout resize is handled autonomously by the HighlightsController).
 */
export const createFillHandleEffect = createEffect(
  ({ controller, getVisibleEntryCount }: FillHandleEffectDeps) => {
    // ---------------------------------------------------------------------------
    // DOM
    // ---------------------------------------------------------------------------
    let scrollableContainer: HTMLElement | null = null;

    // ---------------------------------------------------------------------------
    // Renderers
    // ---------------------------------------------------------------------------
    // Dedicated layer for the fill handle indicator
    let fillHandleLayer: HighlightsRenderer | null = null;

    function ensureDependentElementsAndRenderer() {
      if (!scrollableContainer) {
        scrollableContainer = controller.getScrollContainer();
      }

      if (!fillHandleLayer && scrollableContainer) {
        fillHandleLayer = controller.createLayer();
      }

      return !!(fillHandleLayer && scrollableContainer);
    }

    function syncFillHandleFromState(state: SelectionState, layer: HighlightsRenderer) {
      if (state.selections.length > 0 && state.mode === 'cell') {
        const activeSelection = state.selections[state.selections.length - 1];
        const visibleEntryCount = getVisibleEntryCount();
        // If there are no visible rows, there's nothing to render or compare
        // against. Bail out early to avoid computing a lastRowIndex of -1,
        // which would make comparisons like `bottomRow >= lastRowIndex` true
        // for all bottomRow values.
        if (visibleEntryCount <= 0) {
          layer.clear();
          return;
        }

        const topRow = Math.min(
          activeSelection.anchor.row.index,
          activeSelection.lead.row.index
        );
        const bottomRow = Math.max(
          activeSelection.anchor.row.index,
          activeSelection.lead.row.index
        );
        const lastRowIndex = visibleEntryCount - 1;

        // The fill handle is a cell-only feature. Hide it when all rows are
        // selected — there's no meaningful direction to drag in that case.
        if (topRow === 0 && bottomRow >= lastRowIndex) {
          layer.clear();
          return;
        }

        // When the bottom of the selection is on the last row, the handle at
        // bottom-right would be obscured by the sticky "Add Entry" row (higher
        // stacking context). Move it to the top-right corner instead so it
        // remains visible and the user can still initiate an upward drag.
        const isBottomLastRow = bottomRow >= lastRowIndex;
        const className = isBottomLastRow
          ? `${HIGHLIGHT_FILL_HANDLE_CLASS} ${HIGHLIGHT_FILL_HANDLE_TOP_CLASS}`
          : HIGHLIGHT_FILL_HANDLE_CLASS;

        layer.clear();
        layer.add({
          range: {
            id: 'fill-handle-wrapper',
            anchor: {
              row: isBottomLastRow ? topRow : bottomRow,
              col: Math.max(
                activeSelection.anchor.col.index,
                activeSelection.lead.col.index
              )
            },
            lead: {
              row: isBottomLastRow ? topRow : bottomRow,
              col: Math.max(
                activeSelection.anchor.col.index,
                activeSelection.lead.col.index
              )
            }
          },
          mode: state.mode,
          zIndex: FILL_HANDLE_LAYER_Z_INDEX,
          className
        });
      } else {
        layer.clear();
      }
    }

    return {
      deltaTypes: [
        'selectionModified',
        'selectionReplaced',
        'selectionPreview',
        'cellEditRequested',
        'cellEditStopped'
      ],

      dispose() {
        // Cleanup the fill handle layer
        if (fillHandleLayer) {
          fillHandleLayer.dispose();
          fillHandleLayer = null;
        }

        scrollableContainer = null;
      },

      onDelta(delta, state) {
        if (
          !ensureDependentElementsAndRenderer() ||
          !fillHandleLayer ||
          !scrollableContainer
        ) {
          return;
        }

        if (delta.kind === 'selectionPreview') {
          // we don't want to show this when there is a selection preview
          fillHandleLayer.clear();

          return;
        }

        if (delta.kind === 'cellEditRequested') {
          fillHandleLayer.hideAll();

          return;
        }

        if (delta.kind === 'cellEditStopped') {
          fillHandleLayer.showAll();

          return;
        }

        // Handle Fill Handle placement
        syncFillHandleFromState(state, fillHandleLayer);
      }
    };
  }
);
